import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  ValuationGridData,
  ValuationPeriodState,
} from "../valuation-page.model";
import { faSortUp } from "@fortawesome/free-solid-svg-icons";
import { MatTable } from "@angular/material/table";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/state/app.state";
import {
  DeleteDpParams,
  SetDpParams,
} from "src/app/store/actions/global-subheader.actions";
import { REL_PERIOD_TYPE } from "../valuation-page.constant";
import {
  convertRelativePeriodLabelToId,
  getPeriodTypeDataId,
} from "src/app/utils/period-utils";
import {
  VAIconRegistryService,
  caretDown,
  close,
} from "@va/core-library/icons";

@Component({
  selector: "app-valuation-grid",
  templateUrl: "./valuation-grid.component.html",
  styleUrls: ["./valuation-grid.component.scss"],
})
export class ValuationGridComponent {
  @Input() gridData: ValuationGridData;
  @Input() showMedian: boolean;
  @Input() showAverage: boolean;
  @Input() orientation: string;
  @Input() ratioId: string;
  @Input() tabType: string;
  @Input() currentTicker;
  @Input() ratioList: Array<any>;
  @Input() periodState: ValuationPeriodState;
  @Input() highlightGAAP = false;
  @Input() visible = false;
  @Input() containerClassName = "";
  @Input() isDataInProgress = false;
  @Input() sortByColumnId = "";
  @Output() onDeleteTicker: EventEmitter<any> = new EventEmitter();
  @ViewChild(MatTable) table: MatTable<any>;
  faSortUp = faSortUp;
  dataSource: Array<any> = [];
  displayedColumns = [];
  ratios = [];
  tableWidth = 520;
  periodWidth = 0;
  sortDirection = "";
  activeSort = "";
  sortKey = "";
  ratioOnly = [];
  index: number;
  selectedRow = "";
  selectedCell = "";

  constructor(
    private store: Store<AppState>,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([close, caretDown]);
  }

  // ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    //react to changes in gridData
    if (changes.highlightGAAP) {
      if (
        !changes.highlightGAAP.firstChange &&
        changes.highlightGAAP.currentValue !==
          changes.highlightGAAP.previousValue
      ) {
        return;
      }
    }
    if (changes.visible) {
      if (
        !changes.visible.firstChange &&
        changes.visible.currentValue === false
      ) {
        this.selectedRow = "";
        this.selectedCell = "";
        return;
      }
    }

    if (this.gridData && this.gridData.rowData) {
      this.selectedRow = "";
      this.selectedCell = "";
      this.activeSort = "";
      if (this.sortByColumnId) {
        this.activeSort = this.sortByColumnId;
        this.sortDirection = "desc";
      }
      if (this.tabType === "company") {
        this.dataSource = [...this.ratioList];
        this.ratioOnly = this.gridData.ratios.filter(
          (ratio) => !ratio.startsWith("#"),
        );
        this.index = this.getCompanyIndex(this.gridData, this.currentTicker);
      } else this.dataSource = [...this.gridData.rowData];
      this.ratios = this.gridData.ratios;
      this.displayedColumns = this.getDisplayedColumns(
        this.getDefaultDisplayedColumns(this.orientation, this.tabType),
        this.orientation,
        this.gridData,
      );
      this.periodWidth = this.getPeriodWidth(
        this.gridData.periods,
        this.orientation,
      );
    }
  }

  /**
   * It is used to get default set of columns to show on grid based on orientation
   * @param orientation current orientation of the grid
   * * @param tabType current tab peers/company
   */
  getDefaultDisplayedColumns(
    orientation: string,
    tabType: string,
  ): Array<string> {
    if (orientation === "grid") {
      return ["ticker", "companyName", "lastPrice", "marketCap", "ev"];
    }
    if (tabType === "company") {
      return ["metrics"];
    }
    return ["ticker", "companyName"];
  }

  /**
   * It is used to generate duamic columns to be displayed on the grid based on ratios and periods selected by the user
   * @param defaultColumns List of default columns
   * @param orientation current orientation of grid
   * @param gridData data to be displayed on grid
   */
  getDisplayedColumns(
    defaultColumns: Array<string>,
    orientation: string,
    gridData: ValuationGridData,
  ): Array<string> {
    if (orientation === "grid") {
      return defaultColumns.concat(gridData.ratios);
    }
    return defaultColumns.concat(gridData.periods);
  }

  /**
   * It is used to computw the width of period section dislayed on the grid
   * @param periods List of periods
   * @param orientation current orientation of grid
   */
  getPeriodWidth(periods: Array<string>, orientation: string): number {
    if (orientation === "grid") {
      if (periods.length > 2) {
        return periods.length * 100;
      }
      return 200;
    }
    return 100;
  }

  /**
   * It handles ticker deletion from grid and raises onDeleteTicker event for the parent component to handle it
   * @param ticker ticker to be deleted
   */
  deleteTicker(ticker) {
    this.onDeleteTicker.emit(ticker);
  }

  /**
   * It is used to sort given column of the grid in given direction on peers tab
   * @param colKey key of the column to sort
   * @param sortDirection whether to sort asc or desc directio
   */
  sortData(colKey, sortDirection = "") {
    if (sortDirection) {
      this.sortDirection = sortDirection;
    } else {
      if (colKey === this.activeSort) {
        this.sortDirection =
          this.sortDirection === ""
            ? "asc"
            : this.sortDirection === "asc"
              ? "desc"
              : "";
      } else {
        this.sortDirection = "asc";
      }
    }
    this.activeSort = colKey;
    switch (colKey) {
      case "companyName":
        if (this.sortDirection === "") {
          this.dataSource = [...this.gridData.rowData];
          this.activeSort = "";
        } else {
          this.dataSource.sort((a, b) => {
            if (this.sortDirection === "asc") {
              return a.companyName.localeCompare(b.companyName);
            }

            return b.companyName.localeCompare(a.companyName);
          });
        }
        this.table.renderRows();
        break;
      case "lastPrice":
      case "marketCap":
      case "ev":
        if (this.sortDirection === "") {
          this.dataSource = [...this.gridData.rowData];
          this.activeSort = "";
        } else {
          this.dataSource.sort((a, b) => {
            if (this.sortDirection === "asc") {
              if (a[colKey].raw === undefined && b[colKey].raw === undefined) {
                return 1;
              }
              if (a[colKey].raw === undefined) {
                return -1;
              }
              if (b[colKey].raw === undefined) {
                return 1;
              }
              return a[colKey].raw - b[colKey].raw;
            }
            if (a[colKey].raw === undefined) {
              return 1;
            }
            if (b[colKey].raw === undefined) {
              return -1;
            }
            return b[colKey].raw - a[colKey].raw;
          });
        }
        this.table.renderRows();
        break;
      default:
        //sort for data columns
        if (this.sortDirection === "") {
          this.dataSource = [...this.gridData.rowData];
          this.activeSort = "";
        } else {
          const ratio = colKey.split("|")[0];
          const period = colKey.split("|")[1];
          this.dataSource.sort((a, b) => {
            const aSliData = a[ratio][period]["raw"];
            const bSliData = b[ratio][period]["raw"];

            if (this.sortDirection === "asc") {
              if (aSliData === undefined && bSliData === undefined) {
                return 1;
              }
              if (aSliData === undefined) {
                return -1;
              }
              if (bSliData === undefined) {
                return 1;
              }
              if (aSliData === "NM" && bSliData === "NM") {
                return 1;
              }
              if (aSliData === "NM") {
                return -1;
              }
              if (bSliData === "NM") {
                return 1;
              }
              return aSliData - bSliData;
            }
            if (aSliData === undefined) {
              return 1;
            }
            if (bSliData === undefined) {
              return -1;
            }
            if (aSliData === "NM") {
              return 1;
            }
            if (bSliData === "NM") {
              return -1;
            }
            return bSliData - aSliData;
          });
        }
        this.table && this.table.renderRows();
        break;
    }
  }

  /**
   * This function sorts the grid based on the period columns data on company tab.
   * @param colKey string to identify the column on which is being sorted
   * @param sortDirection asc or desc
   */
  companySortData(colKey, sortDirection = "") {
    if (sortDirection) {
      this.sortDirection = sortDirection;
    } else {
      if (colKey === this.activeSort) {
        this.sortDirection =
          this.sortDirection === ""
            ? "asc"
            : this.sortDirection === "asc"
              ? "desc"
              : "";
      } else {
        this.sortDirection = "asc";
      }
    }
    this.activeSort = colKey;
    switch (colKey) {
      default:
        //sort for data columns
        if (this.sortDirection === "") {
          this.dataSource = [...this.ratioList];
          this.activeSort = "";
        } else {
          const period = colKey;
          this.dataSource.sort((a, b) => {
            const aSliData =
              this.gridData.rowData[0][a["ratio"]][period]["raw"];
            const bSliData =
              this.gridData.rowData[0][b["ratio"]][period]["raw"];

            if (this.sortDirection === "asc") {
              if (aSliData === undefined && bSliData === undefined) {
                return 1;
              }
              if (aSliData === undefined) {
                return -1;
              }
              if (bSliData === undefined) {
                return 1;
              }
              if (aSliData === "NM" && bSliData === "NM") {
                return 1;
              }
              if (aSliData === "NM") {
                return -1;
              }
              if (bSliData === "NM") {
                return 1;
              }
              return aSliData - bSliData;
            }
            if (aSliData === undefined) {
              return 1;
            }
            if (bSliData === undefined) {
              return -1;
            }
            if (aSliData === "NM") {
              return 1;
            }
            if (bSliData === "NM") {
              return -1;
            }
            return bSliData - aSliData;
          });
        }
        this.table && this.table.renderRows();
        break;
    }
  }

  /**
   * returns the index of the obj in rowdata
   * where the company ticker is present
   */
  getCompanyIndex(gridData: ValuationGridData, ticker: string) {
    return gridData.rowData.findIndex((data) => data.ticker === ticker);
  }

  /**
   * It is used to highlight the currently selected row on grid
   * @param rowId identifier of the selected row
   */
  onRowClick(rowId) {
    this.selectedRow = rowId;
    this.selectedCell = "";
  }

  /**
   * It is used to highlight seleted cell on the grid.
   * It is responsible for triggering Formula Bar calls to show formula of the selected cell
   * @param event click event
   * @param data data point on grid that was clicked
   * @param ratio ratio related to the data point
   * @param period period related to the data point
   * @param ticker ticker related to the data point
   * @param rowId identifier of the row of the data point
   * @param currency selected currency
   * @param isForecast identifier whether the data point is forecast or past
   */
  onCellClick(
    event: MouseEvent,
    data,
    ratio: string,
    period,
    ticker,
    rowId,
    currency,
    isForecast,
  ) {
    if (data) {
      event.stopPropagation();
      if (this.selectedCell !== `${ticker}_${ratio}_${period}`) {
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();
        isForecast = isForecast === undefined ? true : isForecast; //default to true since default source is consensus

        const valueType = ratio.includes("denom_yoy") ? "yoy" : "values";

        const dpParams = {
          dp: `${data}|${mm}/${dd}/${yyyy}`, //"16151916582|09/02/2020",
          cut: "STD",
          ticker: ticker,
          growthType: valueType === "values" ? "Analytics" : "YoY%",
          shouldLog: true,
          revision: "",
          source: isForecast ? "consensus" : "va actuals",
          is_universalsliid: true,
        };

        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }
        if (currency && currency !== "Base") {
          dpParams["currency"] = currency;
        }
        this.selectedCell = `${ticker}_${ratio}_${period || ""}`;
        if (!period) {
          period = `CY-${yyyy}`;
        }
        if (
          this.periodState &&
          !(ratio === "lastPrice" || ratio === "marketCap" || ratio === "ev")
        ) {
          const _periodType = getPeriodTypeDataId(this.periodState);
          if (_periodType === REL_PERIOD_TYPE.dataId) {
            dpParams["period_type"] = "relative";
            dpParams["period"] = convertRelativePeriodLabelToId(period);
            dpParams["extras"] = `${data}|${convertRelativePeriodLabelToId(
              period,
            )}`;
          } else {
            dpParams["period"] = period;
            dpParams["extras"] = `${data}|${period}`;
          }
        } else {
          dpParams["period"] = period;
          dpParams["extras"] = `${data}|${period}`;
        }
        this.store.dispatch(new SetDpParams(dpParams));
        this.selectedRow = rowId;
      }
    } else {
      this.selectedCell = "";
      this.store.dispatch(new DeleteDpParams());
    }
  }
}
